// US Navy body-composition estimate. Pure functions, no zod — callable from
// any layer to derive the body-fat readout at render time (we never persist
// it; a corrected measurement must recompute — no denormalised snapshots).
//
// Units are metric: every circumference + height in centimetres, weight in
// kilograms (the app-wide canonical, see units.ts).
//
// Formulae (metric, log10):
//   male:   %BF = 495 / (1.0324  − 0.19077·log10(waist − neck)
//                                 + 0.15456·log10(height)) − 450
//   female: %BF = 495 / (1.29579 − 0.35004·log10(waist + hip − neck)
//                                 + 0.22100·log10(height)) − 450
//   lean mass = weight × (1 − %BF / 100)
//
// The female variant needs hip circumference; the male variant doesn't —
// that's why Profile.sex genuinely drives the math, not just the figure.
// Method error is ~±3.5% vs hydrostatic weighing: this is a TREND tool, so
// the UI surfaces the progression over time, never a clinical verdict.

export type NavySex = "male" | "female";

export type NavyInputs = {
  sex: NavySex;
  heightCm: number;
  neckCm: number;
  waistCm: number;
  // Required for the female formula, ignored for male. A female estimate
  // with hips absent returns null rather than a wrong number.
  hipsCm?: number;
};

const log10 = (x: number): number => Math.log(x) / Math.LN10;

// Body fat can't be negative or approach 100%; for extreme/implausible
// inputs the raw formula can drift outside [0,100]. Clamp to a sane open
// band so the readout never shows a negative % or a NaN-adjacent figure.
// Returns null for a non-finite result so callers render "—".
const clampBodyFat = (bf: number): number | null => {
  if (!Number.isFinite(bf)) return null;
  return Math.min(75, Math.max(2, bf));
};

/**
 * US Navy body-fat % from circumferences + height (all cm). Returns null
 * when the required inputs for the chosen sex are missing or fall outside
 * the formula's valid domain (the log10 arguments must be strictly
 * positive), so the caller shows "—" instead of NaN.
 */
export const bodyFatPercentNavy = (i: NavyInputs): number | null => {
  const { sex, heightCm, neckCm, waistCm, hipsCm } = i;
  if (!(heightCm > 0) || !(neckCm > 0) || !(waistCm > 0)) return null;

  if (sex === "male") {
    const trunk = waistCm - neckCm;
    if (!(trunk > 0)) return null;
    const denom =
      1.0324 - 0.19077 * log10(trunk) + 0.15456 * log10(heightCm);
    if (!(denom > 0)) return null;
    return clampBodyFat(495 / denom - 450);
  }

  if (!(hipsCm !== undefined && hipsCm > 0)) return null;
  const trunk = waistCm + hipsCm - neckCm;
  if (!(trunk > 0)) return null;
  const denom =
    1.29579 - 0.35004 * log10(trunk) + 0.221 * log10(heightCm);
  if (!(denom > 0)) return null;
  return clampBodyFat(495 / denom - 450);
};

/**
 * Lean (fat-free) mass in kg from weight + body-fat %. Returns null on a
 * non-positive weight or a non-finite body-fat input.
 */
export const leanMassKg = (
  weightKg: number,
  bodyFatPercent: number,
): number | null => {
  if (!(weightKg > 0) || !Number.isFinite(bodyFatPercent)) return null;
  return weightKg * (1 - bodyFatPercent / 100);
};
